class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    if (!this.notification) return;
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.headingText = this.notification.querySelector('.cart-notification__heading-text');
    this.headingIcon = this.notification.querySelector('.cart-notification__heading-icon');
    this.headingAdded = this.notification.dataset.headingAdded || '';
    this.headingCart = this.notification.dataset.headingCart || '';
    this.links = this.notification.querySelector('.cart-notification__links');

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
    this.bindRemoveButtons();
    this.setHeaderCartIconAccessibility();
    this.setActionButtonsVisibility(this.hasItemsFromDom());
  }

  setNotificationHeading(mode = 'cart') {
    if (!this.headingText) return;
    const isAdded = mode === 'added';
    this.headingText.textContent = isAdded ? this.headingAdded : this.headingCart;
    if (this.headingIcon) {
      this.headingIcon.toggleAttribute('hidden', !isAdded);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    const openNotification = async (event) => {
      event.preventDefault();
      try {
        await this.refreshSectionsFromServer();
        this.setNotificationHeading('cart');
        this.setActiveElement(cartLink);
        this.open();
      } catch (e) {
        window.location.href = cartLink.getAttribute('href') || window.routes?.cart_url || '/cart';
      }
    };

    cartLink.addEventListener('click', openNotification);
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        openNotification(event);
      }
    });
  }

  async refreshSectionsFromServer() {
    const sectionIds = this.getSectionsToRender()
      .map((section) => section.id)
      .join(',');
    const url = `${window.routes?.cart_url || '/cart'}?sections=${sectionIds}`;
    const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(`Cart notification refresh failed (${response.status})`);
    const sections = await response.json();

    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      const html = sections?.[section.id];
      if (!target || !html) return;
      if (section.selector) {
        target.innerHTML = this.getSectionInnerHTML(html, section.selector);
      } else {
        target.innerHTML = html;
      }
    });
    this.bindRemoveButtons();
    this.setActionButtonsVisibility(this.hasItemsFromSectionHTML(sections?.['cart-icon-bubble']));
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];
      if (!target || !html) return;
      target.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });
    this.bindRemoveButtons();
    this.setNotificationHeading('added');
    this.setActionButtonsVisibility((parsedState.item_count || 0) > 0);

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: '.cart-notification-content',
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  bindRemoveButtons() {
    this.querySelectorAll('[data-cart-remove-keys], [data-cart-remove-key]').forEach((button) => {
      if (button.dataset.boundRemove === '1') return;
      button.dataset.boundRemove = '1';
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        const keysAttr = button.getAttribute('data-cart-remove-keys') || button.getAttribute('data-cart-remove-key') || '';
        const keys = keysAttr
          .split(',')
          .map((key) => key.trim())
          .filter(Boolean);
        if (!keys.length) return;
        button.disabled = true;
        try {
          const updates = {};
          keys.forEach((key) => {
            updates[key] = 0;
          });
          await fetch(window.routes?.cart_update_url || '/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates }),
          });
          await this.refreshSectionsFromServer();
          this.setActionButtonsVisibility(this.hasItemsFromDom());
          if (this.header) this.header.reveal();
        } catch (e) {
          button.disabled = false;
        }
      });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector(selector);
    if (source) return source.innerHTML;
    return parsed.body ? parsed.body.innerHTML : '';
  }

  setActionButtonsVisibility(hasItems) {
    if (!this.links) return;
    this.links.toggleAttribute('hidden', !hasItems);
  }

  hasItemsFromSectionHTML(sectionHtml) {
    if (!sectionHtml) return this.hasItemsFromDom();
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    return Boolean(parsed.querySelector('.cart-count-bubble'));
  }

  hasItemsFromDom() {
    return Boolean(document.querySelector('#cart-icon-bubble .cart-count-bubble'));
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
